import Scene = Phaser.Scene;
import Sprite = Phaser.GameObjects.Sprite;
import BaseSound = Phaser.Sound.BaseSound;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {ChestType, Player} from "@/store/player/types";
import ChestStore from "@/store/chest";
import {ChestContentType, ContentDto} from "@/store/chest/types";
import {ForceUtils} from "@/utils/force-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import {SpaceTr, Tr, TrSpace} from "@/i18n/i18n";
import CreaturePanel from "@/scenes.ui/creature-panel";
import PlayerStore from "@/store/player";
import {PickCreaturePanel} from "@/scenes.ui/pick-creature-panel";
import ListenerManager from "@/managers/listener-manager";
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import SkillUtils from "@/utils/skill-utils";
import BoosterUtils from "@/utils/booster-utils";
import {BoosterType} from "@/store/shop/types";

export class ChestPanel extends VerticalStandardPanel {

    constructor(scene: Scene, canvas: HTMLCanvasElement, player: Player, clickSound: BaseSound | undefined,
                completedSound: BaseSound | undefined) {
        super(scene, canvas, Tr('chst'));
        const self = this;
        this.setDepth(3002);

        let lighting = SpriteUtils.addSprite(this.scene, 0, -90, Keys.OFFER_LIGHT_BACK, undefined, 1.5).setAlpha(0);
        this.add(lighting);

        this.scene!.tweens.add({
            targets: lighting,
            alpha: 0.25,
            duration: 500
        });

        this.scene!.tweens.add({
            targets: lighting,
            angle: 360,
            duration: 120000,
            loop: -1,
        });

        switch (player.chestType) {
            case ChestType.GOLD:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_GOLDEN_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.SILVER:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_SILVER_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.AZURE:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_AZURE_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.LAVA:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_LAVA_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.AMBER:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_AMBER_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.JADE:
                self.init(SpriteUtils.addSprite(self.scene, 0, -90, Keys.CHEST_JADE_CLOSED), player.chestType, clickSound, completedSound);
                break;
            case ChestType.NONE:
        }
        scene.load.start();
    }

    private static getChestText(chestType: ChestType): string {
        switch (chestType) {
            case ChestType.SILVER:
                return Tr('slv.chst');
            case ChestType.GOLD:
                return Tr('gld.chst');
            case ChestType.AZURE:
                return Tr('az.chst');
            case ChestType.LAVA:
                return Tr('lv.chst');
            case ChestType.AMBER:
                return Tr('amb.chst');
            case ChestType.JADE:
                return Tr('jd.chst');
        }
        return Tr('slv.chst');
    }

    private init(chestIcon: Sprite, chestType: ChestType, clickSound: BaseSound | undefined,
                 completedSound: BaseSound | undefined) {
        let self = this;
        let awardComment = TextUtils.addTextByFamily(this.scene, ChestPanel.getChestText(chestType),
            0, 5, chestType === ChestType.SILVER ? '#b9dadb' : '#ffdb5e', 17, 'normal', 450, Fonts.MYRIADPRO_REGULAR);

        let openButton = new SquareButton(this.scene, 0, 150, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('open'), false);

        openButton.setCallback(() => {

            SoundUtils.play(clickSound);
            self.replaceWithOpened(chestType, chestIcon);

            ChestStore.ensureChestContent().then((conts: ContentDto[]) => {
                self.scene!.tweens.add({
                    targets: awardComment,
                    alpha: 0,
                    duration: 200
                });
                self.xButton.setVisible(false);
                self.scene.time.delayedCall(700, () => {
                    SoundUtils.play(completedSound);
                    self.processContent(conts[0], true);
                });
                self.scene.time.delayedCall(2500, () => {
                    SoundUtils.play(completedSound);
                    self.processContent(conts[1], false);
                });

                let takeButton = new SquareButton(this.scene, 0, 150, 0, Keys.UI_ART_GREEN_BUTTON,
                    Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('prinjat'), false);

                takeButton.setCallback(() => {
                    SoundUtils.play(clickSound);

                    let picking: boolean = false;
                    for (let cc of conts) {
                        if (cc.creatureIds && cc.creatureIds.length > 0) {
                            picking = true;

                            let ccs = PlayerStore.getPlayer().creatures.filter(c => cc.creatureIds.includes(c.id));
                            let pickCreaturePanel = new PickCreaturePanel(self.scene, self.canvas, cc, ccs, (cId: number) => {
                                ChestStore.takeChestContent(cc.id, cId).then(() => {
                                    ListenerManager.callCloseChestPanelCallback();
                                    self.setVisible(false);
                                    self.destroy();
                                });
                            })
                            pickCreaturePanel.openAnimated();
                        }
                    }

                    if (!picking) {
                        ChestStore.takeChestContent(conts[0].id, undefined).then(() => {
                            ListenerManager.callCloseChestPanelCallback();
                            self.setVisible(false);
                            self.destroy();
                        });
                    }
                });

                this.add(takeButton);
                takeButton.setAlpha(0);
                SpriteUtils.scale(takeButton, 0.9);

                self.scene!.tweens.add({
                    targets: takeButton,
                    alpha: 1,
                    scale: 1,
                    delay: 3800,
                    duration: 300
                });

            });

            openButton.setVisible(false);

        });

        this.add([chestIcon, awardComment, openButton]);
    }

    private static getOpenedSpriteKey(chestType: ChestType): string {
        switch (chestType) {
            case ChestType.SILVER:
                return Keys.CHEST_SILVER_OPENED;
            case ChestType.GOLD:
                return Keys.CHEST_GOLDEN_OPENED;
            case ChestType.AZURE:
                return Keys.CHEST_AZURE_OPENED;
            case ChestType.LAVA:
                return Keys.CHEST_LAVA_OPENED;
            case ChestType.AMBER:
                return Keys.CHEST_AMBER_OPENED;
            case ChestType.JADE:
                return Keys.CHEST_JADE_OPENED;
        }
        return Keys.CHEST_GOLDEN_OPENED;
    }

    private replaceWithOpened(chestType: ChestType, chestIcon: Sprite) {
        let newChestIcon = SpriteUtils.addSprite(this.scene, -2, -87, ChestPanel.getOpenedSpriteKey(chestType));
        this.add(newChestIcon);
        newChestIcon.setAlpha(0);

        this.scene!.tweens.add({
            targets: chestIcon,
            alpha: 0,
            duration: 600
        });

        this.scene!.tweens.add({
            targets: newChestIcon,
            alpha: 1,
            duration: 600
        });
    }

    private processContent(c: ContentDto, left: boolean) {
        let x = 0;
        let y = -90;
        let xx = left ? -110 : 110;
        let yy = 0;

        let icon;
        let text;

        switch (c.chestContentType) {
            case ChestContentType.GEMS:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_BANK, Keys.UI_SHOP_GEMS_1);
                this.add(icon);
                this.setupText(String(c.gems) + Tr('izumrudov'), xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.FORCE:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_FORCES_LARGE, ForceUtils.getImage(c.force));
                this.add(icon);
                this.setupText(TrSpace('force') + CreatureUtils.getClassNameGenetive(c.force), xx, yy);
                this.tweenPicture(icon, xx, yy, 0.75);
                break;
            case ChestContentType.ATTACK_TO_ALL:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_ATTACK);
                this.add(icon);
                this.setupText(`${Tr('ataka')} + ${SkillUtils.formatValue(c.skillToAll)} ${Tr('all.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.DEFENCE_TO_ALL:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_DEFENCE);
                this.add(icon);
                this.setupText(`${Tr('zaschita')} + ${SkillUtils.formatValue(c.skillToAll)} ${Tr('all.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.HEALTH_TO_ALL:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_HEALTH);
                this.add(icon);
                this.setupText(`${Tr('zdorove')} + ${SkillUtils.formatValue(c.skillToAll)} ${Tr('all.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.ATTACK_TO_ONE:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_ATTACK);
                this.add(icon);
                this.setupText(`${Tr('ataka')} + ${SkillUtils.formatValue(c.skillToOne)} ${Tr('one.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.DEFENCE_TO_ONE:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_DEFENCE);
                this.add(icon);
                this.setupText(`${Tr('zaschita')} + ${SkillUtils.formatValue(c.skillToOne)} ${Tr('one.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.HEALTH_TO_ONE:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, Keys.IC_100_HEALTH);
                this.add(icon);
                this.setupText(`${Tr('zdorove')} +${SkillUtils.formatValue(c.skillToOne)} ${Tr('one.her')}`, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.ARTIFACT:
                icon = SpriteUtils.addSprite(this.scene, x, y, c.artifactAtlas, c.artifactId);
                this.add(icon);
                this.setupText(c.artifactName + ' ' + c.artifactEffect, xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.HERO:
                let creaturePanel = new CreaturePanel(this.scene!, x, y, c.creature, () => {}, false);
                creaturePanel.setHealthVisible();
                this.add([creaturePanel]);
                this.tweenPicture(creaturePanel, xx, yy);
                break;
            case ChestContentType.HEALTH_PERCENT_TO_ALL:
                this.processSkill(x, y, xx, yy, 'zdorove', 'all.her', Keys.IC_100_HEALTH, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.HEALTH_PERCENT_TO_ONE:
                this.processSkill(x, y, xx, yy, 'zdorove', 'one.her', Keys.IC_100_HEALTH, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.ATTACK_PERCENT_TO_ALL:
                this.processSkill(x, y, xx, yy, 'ataka', 'all.her', Keys.IC_100_ATTACK, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.ATTACK_PERCENT_TO_ONE:
                this.processSkill(x, y, xx, yy, 'ataka', 'one.her', Keys.IC_100_ATTACK, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.DEFENCE_PERCENT_TO_ALL:
                this.processSkill(x, y, xx, yy, 'zaschita', 'all.her', Keys.IC_100_DEFENCE, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.DEFENCE_PERCENT_TO_ONE:
                this.processSkill(x, y, xx, yy, 'zaschita', 'one.her', Keys.IC_100_DEFENCE, SkillUtils.formatValue(c.count) + '%');
                break;
            case ChestContentType.ELEPHANT:
                this.processCard(x, y, xx, yy, Keys.CARD_ELEPHANT_SMALL, c.count);
                break;
            case ChestContentType.SNAKE:
                this.processCard(x, y, xx, yy, Keys.CARD_SNAKE_SMALL, c.count);
                break;
            case ChestContentType.EAGLE:
                this.processCard(x, y, xx, yy, Keys.CARD_EAGLE_SMALL, c.count);
                break;
            case ChestContentType.PORTAL:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_PORTALS, Keys.PORTAL_P10);
                this.add(icon);
                this.setupText(Tr('pr.free'), xx, yy);
                this.tweenPicture(icon, xx, yy, 0.4, 0.08);
                break;

            case ChestContentType.AMETIST:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.IC_100_AMETIST);
                this.add(icon);
                this.setupText(String(c.count) + Tr('amtst'), xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.BOOSTER:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_BOOST, BoosterUtils.getIcon(BoosterType.COMPLEX_BIG));
                this.add(icon);
                this.setupText(Tr('mboo'), xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
            case ChestContentType.SLOT_UNLOCK:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_PROFILE, Keys.UI_ART_SLOT);
                this.add(icon);
                this.setupText(Tr('art.slot'), xx, yy);
                this.tweenPicture(icon, xx, yy, 0.4, 0.08);
                break;
            case ChestContentType.HERO_RANK:
                icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_PROFILE, Keys.UI_LEVEL_UP);
                this.add(icon);
                this.setupText(Tr('rang') + '+1', xx, yy);
                this.tweenPicture(icon, xx, yy);
                break;
        }

    }

    private processCard(x: number, y: number, xx: number, yy: number, keyCard: string, count: number) {
        let icon = SpriteUtils.addSprite(this.scene, x, y, keyCard, undefined);
        this.add(icon);
        this.setupText(String(count) + SpaceTr('sst'), xx, yy);
        this.tweenPicture(icon, xx, yy);
    }

    private processSkill(x: number, y: number, xx: number, yy: number, keyTrPre: string, keyTrPost: string, keyImage: string, value: string) {
        let icon = SpriteUtils.addSprite(this.scene, x, y, Keys.ATLAS_IC_100, keyImage);
        this.add(icon);
        this.setupText(`${Tr(keyTrPre)} +${value} ${Tr(keyTrPost)}`, xx, yy);
        this.tweenPicture(icon, xx, yy);
    }

    private setupText(content: string, xx: number, yy: number) {
        let text = TextUtils.addTextByFamily(this.scene, content,
            xx, yy / CU.S + 50, '#ffd956', 12, 'normal', 140, Fonts.MYRIADPRO_BOLD);
        this.add(text);

        SpriteUtils.scale(text, 0.5 / CU.S);
        text.setAlpha(0);
        let g = 30;
        let t0 = 0.45;
        let v = (1 + g*t0*t0/2) / t0;
        this.scene!.tweens.add({
            targets: text,
            delay: 1050,
            duration: t0 * 1000,
            props: {
                alpha: { value: 1.0, ease: 'Linear' },
                scale: { value: 1.0, ease: (t: number) => v*t*t0-g*t*t*t0*t0/2 },
            }
        });
    }

    private tweenPicture(go: CreaturePanel | Sprite, xx: number, yy: number, scale: number = 1.0, initialScale = 0.2) {
        xx = xx * CU.S;
        yy = yy * CU.S;
        go.scale = initialScale / CU.S;
        go.setAlpha(0.1);
        let t0 = 0.95;
        let g = 8;
        let v = (g*t0*t0/2 - 1) / (t0);
        this.scene!.tweens.add({
            targets: go,
            duration: t0 * 1000,
            props: {
                scale: { value: scale, ease: 'Linear' },
                alpha: { value: 1.0, duration: 200, delay: 0, ease: 'Linear' },
                x: { value: xx, ease: 'Linear' },
                y: { value: yy, ease: (t: number) => (- v*(t*t0) + g/2*(t*t0)*(t*t0)) }
            }
        });
    }


}
